import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Newspaper, ArrowRight, MapPin, CalendarDays, Tag, X } from "lucide-react";

export default function Blog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  useSeo({
    title: "Blog local — Bons plans & bonnes adresses en Eure-et-Loir | PROXIreduc-28",
    description: "Guides, bons plans et bonnes adresses à Chartres, Dreux, Châteaudun, Nogent-le-Rotrou… Le blog 100% local de PROXIreduc-28.",
    canonicalPath: "/blog",
    image: "/og-blog.jpg",
  });
  useEffect(() => {
    api.get("/blog?limit=200").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  const cities = useMemo(
    () => Array.from(new Set(items.map((p) => p.city).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr")),
    [items]
  );
  const categories = useMemo(
    () => Array.from(new Set(items.map((p) => p.category).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr")),
    [items]
  );

  const filtered = useMemo(
    () => items.filter((p) => (!city || p.city === city) && (!category || p.category === category)),
    [items, city, category]
  );

  const hasFilter = city || category;

  return (
    <div data-testid="blog-page">
      <section className="bg-gradient-to-br from-orange-50 via-white to-emerald-50/40 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#E05638]">
            <Newspaper size={14} /> Le blog local
          </div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold text-slate-900">
            Bons plans & bonnes adresses en Eure-et-Loir
          </h1>
          <p className="mt-3 text-base text-slate-600 max-w-2xl">
            Nos guides pour bien consommer local à Chartres, Dreux, Châteaudun et partout dans le 28 :
            restaurants, boutiques, sorties, astuces pour payer moins cher près de chez vous.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* ===== FILTRES Ville & Thème ===== */}
        {!loading && (cities.length > 0 || categories.length > 0) && (
          <div className="mb-8 space-y-4" data-testid="blog-filters">
            {cities.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                  <MapPin size={12} /> Par ville
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setCity("")}
                    data-testid="blog-city-all"
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${!city ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                  >
                    Toutes les villes
                  </button>
                  {cities.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCity(c)}
                      data-testid={`blog-city-${c}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${city === c ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {categories.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                  <Tag size={12} /> Par thème
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setCategory("")}
                    data-testid="blog-category-all"
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${!category ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                  >
                    Tous les thèmes
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCategory(c)}
                      data-testid={`blog-category-${c}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${category === c ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {hasFilter && (
              <button
                onClick={() => { setCity(""); setCategory(""); }}
                data-testid="blog-reset-filters"
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#E05638] transition-colors"
              >
                <X size={14} /> Réinitialiser les filtres
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-slate-500">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="blog-empty">
            {hasFilter
              ? "Aucun article ne correspond à ces filtres pour le moment."
              : "Nos premiers articles arrivent très bientôt. Revenez vite !"}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {filtered.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                    className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="aspect-[1.91/1] bg-slate-100 overflow-hidden">
                  {p.cover_image
                    ? <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full grid place-items-center text-[#E05638]/40"><Newspaper size={40} /></div>}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                    {p.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.city}</span>}
                    {p.category && <span className="inline-flex items-center gap-1"><Tag size={12} /> {p.category}</span>}
                    {p.published_at && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {new Date(p.published_at).toLocaleDateString("fr-FR")}</span>}
                  </div>
                  <h2 className="mt-2 font-heading text-lg font-bold text-slate-900 group-hover:text-[#E05638] transition-colors">{p.title}</h2>
                  {p.excerpt && <p className="mt-1.5 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>}
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#E05638]">Lire l'article <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
